import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'

import ConnectionsDrawer from '../../accounts/components/ConnectionsDrawer'
import BubbleChart from './BubbleChart'

import {
  ANALYTICS_EVENTS,
  useBudgetsStore,
  useEvent,
  useGlobalUiStore,
  useWidgetContainerProvider,
  useScreenSize,
  useGlobalCopyStore,
} from '../../common'
import { type DetailedBudget } from '../../core'

import { BudgetsEmptyState } from './BudgetsEmptyState'

interface BubbleBudgetsProps {
  createBudgetOnClick?: () => void
  height: number
  isDraggable?: boolean
  isMiniWidget?: boolean
  shouldShowZeroState: boolean
  unavailableWidth?: number
}

export const BubbleBudgets: React.FC<BubbleBudgetsProps> = observer(
  ({
    createBudgetOnClick = () => {},
    height,
    isDraggable = false,
    isMiniWidget = false,
    shouldShowZeroState,
    unavailableWidth = 24,
  }) => {
    const { onEvent } = useEvent()
    const { setSelectedBudget } = useBudgetsStore()
    const { isInitialized } = useGlobalUiStore()
    const { accounts: accountsCopy } = useGlobalCopyStore()
    const { availableWidth } = useWidgetContainerProvider()
    const { isMobile } = useScreenSize()

    const [showConnectionsWidget, setShowConnectionsWidget] = React.useState(false)

    const width = React.useMemo(() => {
      return availableWidth - unavailableWidth
    }, [availableWidth])

    const handleBubbleClick = (budget: DetailedBudget) => {
      setSelectedBudget(budget)
      onEvent(ANALYTICS_EVENTS.BUDGETS_CLICK_BUDGET_CATEGORY, {
        budget_category: budget.category.name,
        click_type: 'bubble',
      })
    }

    const handleManageConnections = () => {
      setShowConnectionsWidget(true)
      onEvent(ANALYTICS_EVENTS.ACCOUNTS_CLICK_CONNECT)
    }

    return (
      <Box
        ml={shouldShowZeroState || isMobile ? 0 : 24}
        sx={{ alignSelf: 'center', ...(isMiniWidget && { height: '100%' }) }}
      >
        {shouldShowZeroState || !isInitialized ? (
          <BudgetsEmptyState
            createBudgetOnClick={createBudgetOnClick}
            isMiniWidget={isMiniWidget}
            onConnectAccountsClick={handleManageConnections}
          />
        ) : (
          <BubbleChart
            height={height}
            isDraggable={isDraggable}
            onClick={handleBubbleClick}
            width={width}
          />
        )}

        <ConnectionsDrawer
          onClose={() => setShowConnectionsWidget(false)}
          showConnectionsWidget={showConnectionsWidget}
          title={accountsCopy.manage_connections}
        />
      </Box>
    )
  },
)
